

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Grid,
  Typography,
  Box,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  LinearProgress,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Error,
  Warning,
  Schedule,
  Queue,
  Timeline,
  Speed,
  Assessment,
  Monitor
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { formatDate } from 'utils/DateTimeConversion';

const statusColors = {
  success: 'success',
  failed: 'error',
  warning: 'warning',
  pending: 'info',
  processing: 'primary'
};

const statusIcons = {
  success: <CheckCircle />,
  failed: <Error />,
  warning: <Warning />,
  pending: <Schedule />,
  processing: <CircularProgress size={16} />
};

function AutomationAnalytics({ data, loading, onRefresh }) {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderMetricsOverview = () => {
    if (!data?.metrics) return null;

    const { metrics } = data;
    const todayMetrics = metrics.today || {};
    const weeklyMetrics = metrics.weekly || {};
    const monthlyMetrics = metrics.monthly || {};

    return (
      <Grid container spacing={3}>
        {/* Today's Metrics */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Today's Performance"
              titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
              avatar={<Timeline color="primary" />}
            />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {todayMetrics.total_executions || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Executions
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  {todayMetrics.success_rate || 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Success Rate
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Avg Time: {todayMetrics.avg_execution_time || 0}ms
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Weekly Metrics */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Weekly Performance"
              titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
              avatar={<TrendingUp color="success" />}
            />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {weeklyMetrics.total_executions || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Executions
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  {weeklyMetrics.success_rate || 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Success Rate
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Avg Time: {weeklyMetrics.avg_execution_time || 0}ms
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Metrics */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Monthly Performance"
              titleTypographyProps={{ variant: 'h6', fontWeight:'bold' }}
              avatar={<Assessment color="info" />}
            />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h4" color="info.main" fontWeight="bold">
                  {monthlyMetrics.total_executions || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Executions
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  {monthlyMetrics.success_rate || 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Success Rate
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Avg Time: {monthlyMetrics.avg_execution_time || 0}ms
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderQueueStatus = () => {
    if (!data?.queue) return null;

    const { queue } = data;
    const queueItems = queue.queue_items || [];
    const queueHealth = queue.health_overview || {};

    return (
      <Grid container spacing={3}>
        {/* Queue Health Overview */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Queue Health"
              titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
              avatar={<Monitor color="primary" />}
            />
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Overall Health</Typography>
                  <Chip
                    label={queueHealth.overall_status || 'Unknown'}
                    color={queueHealth.overall_status === 'healthy' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={queueHealth.health_score || 0}
                  color={queueHealth.health_score > 70 ? 'success' : queueHealth.health_score > 40 ? 'warning' : 'error'}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Health Score: {queueHealth.health_score || 0}%
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Queue Statistics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="h6" color="primary">
                      {queueHealth.total_items || 0}
                    </Typography>
                    <Typography variant="caption">Total Items</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6" color="success.main">
                      {queueHealth.processing_items || 0}
                    </Typography>
                    <Typography variant="caption">Processing</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6" color="warning.main">
                      {queueHealth.pending_items || 0}
                    </Typography>
                    <Typography variant="caption">Pending</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6" color="error.main">
                      {queueHealth.failed_items || 0}
                    </Typography>
                    <Typography variant="caption">Failed</Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Queue Performance */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Queue Performance"
              titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
              avatar={<Speed color="success" />}
            />
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Processing Speed
                </Typography>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {queueHealth.avg_processing_time || 0}ms
                </Typography>
                <Typography variant="caption">Average Processing Time</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Throughput
                </Typography>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {queueHealth.items_per_minute || 0}
                </Typography>
                <Typography variant="caption">Items per Minute</Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Error Rate
                </Typography>
                <Typography variant="h6" color="error.main" fontWeight="bold">
                  {queueHealth.error_rate || 0}%
                </Typography>
                <Typography variant="caption">Failed Items Rate</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderQueueItems = () => {
    if (!data?.queue?.queue_items) return null;

    const queueItems = data.queue.queue_items;

    return (
      <Card>
        <CardHeader
          title="Queue Items"
          titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
          avatar={<Queue color="primary" />}
        />
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Automation</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Processing Time</TableCell>
                  <TableCell>Retry Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {queueItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {item.automation_name || `Automation ${item.automation_id}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={statusIcons[item.status] || statusIcons.pending}
                        label={item.status}
                        color={statusColors[item.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.priority || 'Normal'}
                        color={item.priority === 'high' ? 'error' : item.priority === 'medium' ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(item.created_at, 'datetime')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.processing_time ? `${item.processing_time}ms` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.retry_count || 0}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const renderPerformanceTrends = () => {
    if (!data?.metrics?.performance_trends) return null;

    const trends = data.metrics.performance_trends;

    return (
      <Card>
        <CardHeader
          title="Performance Trends"
          titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
          avatar={<TrendingUp color="info" />}
        />
        <CardContent>
          <Grid container spacing={3}>
            {trends.map((trend, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {trend.metric_name}
                  </Typography>
                  <Typography variant="h6" color="primary" gutterBottom>
                    {trend.current_value}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {trend.trend_direction === 'up' ? (
                      <TrendingUp color="success" fontSize="small" />
                    ) : (
                      <TrendingDown color="error" fontSize="small" />
                    )}
                    <Typography
                      variant="body2"
                      color={trend.trend_direction === 'up' ? 'success.main' : 'error.main'}
                    >
                      {trend.change_percentage}% from last period
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </MDBox>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No analytics data available. Click refresh to load data.
      </Alert>
    );
  }

  return (
    <MDBox>
      {/* Header with Refresh Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Automation Analytics Dashboard
        </Typography>
        <MDButton
          variant="outlined"
          startIcon={<Refresh />}
          onClick={onRefresh}
        >
          Refresh Data
        </MDButton>
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ px: 2 }}>
          <Tab label="Overview" icon={<Assessment />} iconPosition="start" />
          <Tab label="Queue Status" icon={<Queue />} iconPosition="start" />
          <Tab label="Performance Trends" icon={<TrendingUp />} iconPosition="start" />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          {renderMetricsOverview()}
          <Box sx={{ mt: 3 }}>
            {renderQueueStatus()}
          </Box>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {renderQueueStatus()}
          <Box sx={{ mt: 3 }}>
            {renderQueueItems()}
          </Box>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          {renderPerformanceTrends()}
        </Box>
      )}
    </MDBox>
  );
}

export default AutomationAnalytics;
